import { useState, useRef, useEffect, useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLang } from "@/hooks/use-lang";
import ChatMessage from "@/components/ChatMessage";
import ChatDatePicker from "@/components/ChatDatePicker";
import TravelTypePicker from "@/components/TravelTypePicker";
import CityPicker from "@/components/CityPicker";

export interface Msg {
  role: "user" | "assistant";
  content: string;
}

interface ChatPanelProps {
  messages: Msg[];
  onSend: (input: string) => void;
  isLoading: boolean;
  isGeneratingTrip?: boolean;
  hasTravelType?: boolean;
}

const DATE_KEYWORDS = [
  "date de voyage", "dates de voyage", "travel dates", "dates du voyage",
  "date de départ", "departure date", "date de retour", "return date",
  "quand souhaitez-vous partir", "when do you want to travel",
  "quand voulez-vous partir", "when would you like to go",
  "quand partez-vous", "when are you leaving",
  "sélectionne tes dates", "select your dates",
  "choisissez vos dates", "choose your dates",
  "calendrier", "calendar",
];

const DATE_EXCLUDE_KEYWORDS = [
  "ville", "city", "destination", "pays", "country",
  "type de voyage", "travel style", "travel type",
  "climat", "climate", "météo", "weather",
  "quelle ville", "which city", "what city",
  "dans quelle ville", "where exactly", "où exactement",
];

const TRAVEL_TYPE_KEYWORDS = [
  "type de voyage", "quel type", "style de voyage", "genre de voyage",
  "what type of trip", "what kind of trip", "travel style", "type of travel",
  "détente", "aventure", "culture", "mixte",
  "relaxation", "adventure", "mixed",
  "préfères-tu", "do you prefer",
  "quel style", "what style",
];

// Detect if user is in "Je sais où aller" flow (country already chosen, city not yet confirmed)
function isInKnowWhereFlow(messages: { role: string; content: string }[]): boolean {
  if (messages.length === 0) return false;
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser) return false;
  const lower = firstUser.content.toLowerCase();
  const isKnowWhere = lower.includes("je veux voyager") || lower.includes("i want to travel");
  if (!isKnowWhere) return false;
  // Check if city has been confirmed (user replied after city question)
  const cityQuestionIndex = messages.findIndex(
    (m, i) => m.role === "assistant" && i > 0 && CITY_KEYWORDS.some((kw) => m.content.toLowerCase().includes(kw))
  );
  if (cityQuestionIndex === -1) return true; // City question not asked yet → still in flow
  // Check if user replied after the city question
  const userReplyAfterCity = messages.slice(cityQuestionIndex + 1).some((m) => m.role === "user");
  return !userReplyAfterCity; // If no reply yet, still waiting for city
}

const CITY_KEYWORDS = [
  "quelle ville", "dans quelle ville", "which city", "what city",
  "ville souhaitez", "ville préférez", "ville aimeriez",
  "city would you", "city do you", "city are you",
  "destination précise", "specific destination",
  "where exactly", "où exactement",
  "ville de destination", "destination city",
  "ville voulez", "ville veux",
  "city to visit", "visit in",
  "ville visiter", "ville aller",
];

const CITY_EXCLUDE_KEYWORDS = [
  "date", "quand", "when", "calendrier", "calendar",
  "type de voyage", "travel style", "quel type", "what type",
];

function shouldShowCityPicker(messages: { role: string; content: string }[]): boolean {
  if (messages.length === 0) return false;
  const last = messages[messages.length - 1];
  if (last.role !== "assistant") return false;
  const lower = last.content.toLowerCase();
  if (CITY_EXCLUDE_KEYWORDS.some((kw) => lower.includes(kw))) return false;
  return CITY_KEYWORDS.some((kw) => lower.includes(kw));
}

function extractCountryHint(messages: { role: string; content: string }[]): string {
  // Search recent messages for country mentions
  return messages
    .slice(-6)
    .map((m) => m.content)
    .join(" ");
}

function shouldShowDatePicker(messages: Msg[]): boolean {
  if (messages.length === 0) return false;
  const last = messages[messages.length - 1];
  if (last.role !== "assistant") return false;
  const lower = last.content.toLowerCase();
  if (DATE_EXCLUDE_KEYWORDS.some((kw) => lower.includes(kw))) return false;
  return DATE_KEYWORDS.some((kw) => lower.includes(kw));
}

function shouldShowTypePicker(messages: Msg[]): boolean {
  if (messages.length === 0) return false;
  // NEVER show travel type picker if we're in "Je sais où aller" flow
  if (isInKnowWhereFlow(messages)) return false;
  const last = messages[messages.length - 1];
  if (last.role !== "assistant") return false;
  const lower = last.content.toLowerCase();
  return TRAVEL_TYPE_KEYWORDS.some((kw) => lower.includes(kw));
}

const ChatPanel = ({ messages, onSend, isLoading, isGeneratingTrip = false, hasTravelType = false }: ChatPanelProps) => {
  const { t, lang } = useLang();
  const [input, setInput] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerDismissed, setDatePickerDismissed] = useState(false);
  const [typePickerDismissed, setTypePickerDismissed] = useState(false);
  const [cityPickerDismissed, setCityPickerDismissed] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const showDatePicker = useMemo(() => {
    if (datePickerDismissed || isLoading) return false;
    return shouldShowDatePicker(messages);
  }, [messages, isLoading, datePickerDismissed]);

  const showTypePicker = useMemo(() => {
    if (hasTravelType || typePickerDismissed || isLoading) return false;
    if (showDatePicker) return false;
    return shouldShowTypePicker(messages);
  }, [messages, isLoading, typePickerDismissed, showDatePicker, hasTravelType]);

  const showCityPicker = useMemo(() => {
    if (cityPickerDismissed || isLoading) return false;
    if (showDatePicker || showTypePicker) return false;
    return shouldShowCityPicker(messages);
  }, [messages, isLoading, cityPickerDismissed, showDatePicker, showTypePicker]);

  const countryHint = useMemo(() => {
    if (!showCityPicker) return "";
    return extractCountryHint(messages);
  }, [showCityPicker, messages]);

  // Auto-open date picker when detected
  useEffect(() => {
    if (showDatePicker && !datePickerOpen) {
      setDatePickerOpen(true);
      setDatePickerDismissed(false);
    }
    if (!showDatePicker) {
      setDatePickerOpen(false);
    }
  }, [showDatePicker]);

  // Reset dismissed state when messages change significantly
  useEffect(() => {
    setDatePickerDismissed(false);
    setTypePickerDismissed(false);
    setCityPickerDismissed(false);
  }, [messages.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    onSend(input.trim());
    setInput("");
  };

  const handleDateConfirm = (formatted: string) => {
    setDatePickerOpen(false);
    setDatePickerDismissed(true);
    onSend(formatted);
  };

  const handleDateDismiss = () => {
    setDatePickerOpen(false);
    setDatePickerDismissed(true);
  };

  const handleTypeSelect = (type: string) => {
    setTypePickerDismissed(true);
    onSend(type);
  };

  const handleCitySelect = (city: string) => {
    setCityPickerDismissed(true);
    onSend(city);
  };

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 px-4 py-4">
        <div className="space-y-4">
          {messages.map((m, i) => (
            <ChatMessage key={i} role={m.role} content={m.content} />
          ))}
          {isLoading && messages[messages.length - 1]?.role === "user" && (
            <div className="flex justify-start">
              <div className="bg-card border border-border/60 rounded-2xl rounded-bl-md px-4 py-3 shadow-card">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:0ms]" />
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:150ms]" />
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}
          {isGeneratingTrip && (
            <div className="flex justify-start">
              <div className="bg-card border border-border/60 rounded-2xl rounded-bl-md px-5 py-4 shadow-card space-y-3 w-full max-w-[85%] animate-in fade-in duration-300">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-sm font-semibold text-foreground">
                    {lang === "fr" ? "Préparation de votre voyage…" : "Building your trip…"}
                  </span>
                </div>
                <div className="space-y-2.5">
                  <Skeleton className="h-14 w-full rounded-xl" />
                  <Skeleton className="h-14 w-full rounded-xl" />
                  <Skeleton className="h-14 w-[80%] rounded-xl" />
                </div>
              </div>
            </div>
          )}
          {showTypePicker && (
            <TravelTypePicker onSelect={handleTypeSelect} />
          )}
          {showCityPicker && (
            <CityPicker onSelect={handleCitySelect} countryHint={countryHint} />
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      <form onSubmit={handleSubmit} className="border-t border-border/50 p-4 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t.inputPlaceholder}
          disabled={isLoading}
          className="h-12 text-base flex-1"
        />
        <Button
          type="submit"
          size="icon"
          disabled={!input.trim() || isLoading}
          className="h-12 w-12 shrink-0"
        >
          <Send className="w-5 h-5" />
        </Button>
      </form>

      {datePickerOpen && (
        <ChatDatePicker
          lang={lang}
          onConfirm={handleDateConfirm}
          onDismiss={handleDateDismiss}
        />
      )}
    </div>
  );
};

export default ChatPanel;
